// Wrappers do Firebase Auth. Centraliza tudo que o resto do app precisa:
// observar o estado, fazer login/logout, cadastrar, resetar senha.
//
// Nada aqui conhece UI — a tela de login importa estas funções e só
// apresenta erros do usuário.

import {
  EmailAuthProvider,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  deleteUser,
  getAdditionalUserInfo,
  onAuthStateChanged,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
} from 'firebase/auth';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';

import { auth, db } from './firebase.js';
import { API } from './api.js';

// ---------- observação ----------

/**
 * Observa mudanças de autenticação. Retorna unsubscribe.
 * @param {(user: import('firebase/auth').User | null) => void} cb
 */
export function observeAuth(cb) {
  return onAuthStateChanged(auth, cb);
}

/**
 * Resolve na primeira emissão do onAuthStateChanged — útil para decidir
 * redirect antes de renderizar a página.
 */
export function waitForAuth() {
  return new Promise((resolve) => {
    const unsub = onAuthStateChanged(auth, (user) => {
      unsub();
      resolve(user);
    });
  });
}

export function getCurrentUser() {
  return auth.currentUser;
}

/**
 * Retorna o ID token (JWT) do usuário logado, pronto pra ir num header
 * `Authorization: Bearer ...`. Firebase refresh automaticamente quando
 * perto da expiração; `getIdToken(true)` força refresh.
 *
 * Lança se não há usuário logado.
 */
export async function getIdToken(forceRefresh = false) {
  const user = auth.currentUser || await waitForAuth();
  if (!user) throw new Error('Sem usuário logado — não dá pra obter ID token.');
  return await user.getIdToken(forceRefresh);
}

/**
 * Helper pra construir `fetch(...)` com Authorization Bearer preenchido.
 * Pode passar um init normal (headers/body/method/etc) — o Authorization
 * é adicionado/sobrescrito.
 */
export async function authFetch(url, init = {}) {
  const token = await getIdToken();
  const headers = new Headers(init.headers || {});
  headers.set('Authorization', `Bearer ${token}`);
  return fetch(url, { ...init, headers });
}

// ---------- login/signup/reset ----------

export async function signInEmail(email, password) {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  await ensureUserDoc(cred.user);
  return cred.user;
}

export async function signUpEmail(email, password) {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  await ensureUserDoc(cred.user);
  // Dispara verificação — não bloqueia login, mas UI pode mostrar aviso
  try {
    await sendEmailVerification(cred.user);
  } catch (e) {
    console.warn('[auth] falha ao enviar email de verificação:', e.message);
  }
  return cred.user;
}

export async function signInGoogle() {
  // No app (Capacitor) o popup do Firebase não funciona dentro do WebView —
  // usa o login nativo do Google via plugin. No site, popup normal.
  if (window.__MAPIARE_NATIVE__) {
    return signInGoogleNative();
  }
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  const cred = await signInWithPopup(auth, provider);
  await ensureUserDoc(cred.user);
  // isNewUser = primeiro login com esse Google (conta acabou de ser criada).
  // A tela de login usa isso pra tratar como cadastro (boas-vindas).
  const isNewUser = !!getAdditionalUserInfo(cred)?.isNewUser;
  return { user: cred.user, isNewUser };
}

/**
 * Login Google no app: o plugin faz o fluxo nativo do Google e devolve um
 * idToken; a gente troca esse token por uma credencial do Firebase e faz o
 * signIn no SDK JS (mesmo `auth` que o resto do app observa).
 *
 * Import dinâmico de propósito: o plugin nativo NUNCA é carregado no site
 * (só é chamado quando window.__MAPIARE_NATIVE__ é true). O pacote precisa
 * estar instalado (`npm i @capacitor-firebase/authentication`) pro build
 * resolver o chunk — no site o chunk existe mas nunca é baixado.
 */
async function signInGoogleNative() {
  const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
  // useCredentialManager:false → usa o fluxo clássico GoogleSignIn (abre o
  // seletor de contas). O Credential Manager (padrão) só mostra contas que já
  // autorizaram o app, então na 1ª vez retorna "no credentials available".
  const result = await FirebaseAuthentication.signInWithGoogle({ useCredentialManager: false });
  const idToken = result?.credential?.idToken;
  if (!idToken) throw new Error('Login Google nativo não retornou idToken.');
  const credential = GoogleAuthProvider.credential(idToken);
  const cred = await signInWithCredential(auth, credential);
  await ensureUserDoc(cred.user);
  const isNewUser = !!getAdditionalUserInfo(cred)?.isNewUser;
  return { user: cred.user, isNewUser };
}

export function resetPassword(email) {
  return sendPasswordResetEmail(auth, email);
}

export function signOut() {
  return fbSignOut(auth);
}

export function resendVerification() {
  if (!auth.currentUser) throw new Error('Não autenticado');
  return sendEmailVerification(auth.currentUser);
}

// ---------- exclusão de conta ----------

/**
 * Como o usuário se autentica: 'password', 'google.com' ou null (sem login).
 * A tela de exclusão usa isso pra decidir se pede senha ou reautenticação
 * pelo Google.
 */
export function primaryProviderId() {
  const user = auth.currentUser;
  if (!user) return null;
  const ids = (user.providerData || []).map((p) => p.providerId);
  if (ids.includes('password')) return 'password';
  if (ids.includes('google.com')) return 'google.com';
  return ids[0] || null;
}

/**
 * Reautentica o usuário — o Firebase exige login recente pra operações
 * sensíveis (deleteUser). Lança erro do Firebase se a senha estiver errada
 * ou o usuário cancelar o Google.
 *
 * @param {{ password?: string }} opts
 */
export async function reauthenticate({ password } = {}) {
  const user = auth.currentUser;
  if (!user) throw new Error('Não autenticado');
  const provider = primaryProviderId();

  if (provider === 'password') {
    if (!password) {
      const err = new Error('Informe a senha.');
      err.code = 'auth/missing-password';
      throw err;
    }
    const cred = EmailAuthProvider.credential(user.email, password);
    return await reauthenticateWithCredential(user, cred);
  }

  if (provider === 'google.com') {
    if (window.__MAPIARE_NATIVE__) {
      // Mesmo caminho do login nativo: o plugin devolve um idToken novo, que
      // vira credencial do Firebase. Popup não funciona no WebView.
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      const result = await FirebaseAuthentication.signInWithGoogle({ useCredentialManager: false });
      const idToken = result?.credential?.idToken;
      if (!idToken) throw new Error('Google não retornou idToken.');
      return await reauthenticateWithCredential(user, GoogleAuthProvider.credential(idToken));
    }
    return await reauthenticateWithPopup(user, new GoogleAuthProvider());
  }

  throw new Error('Provedor de login não suportado para exclusão.');
}

/**
 * Prévia do que a exclusão vai apagar (nº de viagens próprias e de terceiros).
 * Falha silenciosa: se o backend não responder, o modal mostra o texto
 * genérico em vez de travar.
 */
export async function fetchDeletionPreview() {
  try {
    const res = await authFetch(API.deleteAccount());
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * Exclui a conta permanentemente, em três etapas:
 *   1. reautentica (exigência do Firebase pra deleteUser);
 *   2. backend apaga os DADOS (viagens, convites, arquivos, doc do usuário);
 *   3. cliente apaga o USUÁRIO do Firebase Auth.
 *
 * A ordem importa: se apagássemos o usuário do Auth primeiro, perderíamos o
 * token e o backend não conseguiria mais autenticar a limpeza dos dados.
 *
 * @param {{ password?: string }} opts senha, quando o login é email/senha
 * @returns {Promise<object>} relatório do backend
 */
export async function deleteAccount({ password } = {}) {
  const user = auth.currentUser;
  if (!user) throw new Error('Não autenticado');

  await reauthenticate({ password });

  const res = await authFetch(API.deleteAccount('EXCLUIR'), { method: 'DELETE' });
  if (!res.ok) {
    let message = 'Não foi possível excluir a conta agora.';
    try { message = (await res.json())?.error || message; } catch {}
    throw new Error(message);
  }
  const report = await res.json();

  // Dados já foram apagados. Se esta última etapa falhar (rede caiu), o
  // usuário fica com um login órfão sem dados — logamos e seguimos pro
  // logout; a próxima tentativa de exclusão só refaz esta etapa.
  try {
    await deleteUser(user);
  } catch (e) {
    console.error('[deleteAccount] deleteUser falhou:', e?.code, e?.message);
  }

  return report;
}

// ---------- perfil ----------

/**
 * Idempotente: cria users/{uid} no primeiro login. Usa merge pra não
 * sobrescrever alterações futuras do perfil.
 */
async function ensureUserDoc(user) {
  if (!user) return;
  const ref = doc(db, 'users', user.uid);
  await setDoc(
    ref,
    {
      email:        user.email || null,
      displayName:  user.displayName || null,
      photoURL:     user.photoURL || null,
      providerIds:  (user.providerData || []).map(p => p.providerId),
      lastLoginAt:  serverTimestamp(),
      // só define createdAt se o doc ainda não existir — merge preserva o valor antigo
      createdAt:    serverTimestamp(),
    },
    { merge: true },
  );
}

// ---------- mensagens amigáveis ----------

/**
 * Traduz códigos de erro do Firebase Auth para mensagens em português.
 * Códigos em: https://firebase.google.com/docs/reference/js/auth#autherrorcodes
 */
export function authErrorMessage(err) {
  const code = err?.code || '';
  const map = {
    'auth/invalid-credential':        'Email ou senha incorretos.',
    'auth/invalid-email':             'Email inválido.',
    'auth/user-disabled':             'Esta conta foi desativada.',
    'auth/user-not-found':            'Email ou senha incorretos.',
    'auth/wrong-password':            'Email ou senha incorretos.',
    'auth/email-already-in-use':      'Este email já tem cadastro. Tente fazer login.',
    'auth/weak-password':             'Senha fraca — use ao menos 8 caracteres.',
    'auth/popup-closed-by-user':      'Login com Google cancelado.',
    'auth/popup-blocked':             'Popup do Google bloqueado pelo navegador.',
    'auth/network-request-failed':    'Sem conexão com a internet.',
    'auth/too-many-requests':         'Muitas tentativas. Aguarde alguns minutos.',
    'auth/operation-not-allowed':     'Método de login não habilitado no projeto.',
    'auth/missing-password':          'Informe a senha.',
    'auth/requires-recent-login':     'Por segurança, faça login de novo antes de excluir a conta.',
    'auth/user-mismatch':             'Essa conta do Google é diferente da que está logada.',
    'auth/cancelled-popup-request':   'Confirmação com o Google cancelada.',
  };
  return map[code] || err?.message || 'Erro desconhecido.';
}
